/**
 * Plain-text cleanup for agent-generated display strings (conversation,
 * canvas, and surface titles, list previews). Agents sometimes emit titles
 * with markdown emphasis (`**Ownership Inquiry**`) or HTML entities
 * (`Performance &amp; Outcomes`); anywhere the UI renders the string as raw
 * text — sidebar rows, tooltips, panel headers, chips — it should pass
 * through {@link cleanDisplayText} first.
 *
 * This is the single shared implementation: the older local copies in
 * inbox/preview-utils, AuiText and AuiSurface delegate here.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
};

/**
 * Decode common HTML entities (named, decimal, hex) to their characters.
 * Dependency-free and safe for text rendered via JSX (never innerHTML).
 * Decodes `&amp;` last-ish naturally: a double-escaped `&amp;amp;` becomes
 * `&amp;` after one pass, which is the faithful single-decode behavior.
 */
export function decodeHtmlEntities(input: string): string {
  if (!input || !input.includes('&')) return input ?? '';
  return input.replace(
    /&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, body: string) => {
      if (body[0] === '#') {
        const isHex = body[1] === 'x' || body[1] === 'X';
        const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff
          ? String.fromCodePoint(code)
          : match;
      }
      return NAMED_ENTITIES[body.toLowerCase()] ?? match;
    },
  );
}

/**
 * Flatten markdown to a single plain-text line. Drops fenced code blocks,
 * table rows, blockquote / heading / list markers and inline emphasis;
 * keeps link labels and inline-code contents. Falls through to the raw
 * string when it doesn't look like markdown at all.
 */
export function stripMarkdownToLine(input: string): string {
  if (!input) return '';
  let text = input;

  // Fenced code blocks — drop entirely (a wall of code in a one-line
  // context adds noise without ever being useful).
  text = text.replace(/```[\s\S]*?```/g, ' ');

  // Inline code: keep the contents, drop the backticks.
  text = text.replace(/`([^`]+)`/g, '$1');

  // Images — drop, keep alt text.
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  // Links — keep label.
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');

  // Process line-by-line for block-level markers.
  text = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => !line.startsWith('|') && !/^\s*[-:]+\s*\|/.test(line))
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '')
        .replace(/^>\s*/, '')
        .replace(/^\s*[-*+]\s+/, '')
        .replace(/^\s*\d+\.\s+/, ''),
    )
    .join(' ');

  // Inline emphasis.
  text = text
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1');

  // Collapse whitespace.
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * The one-stop cleanup for titles and other single-line plain-text renders:
 * decode HTML entities, then flatten markdown to one line.
 */
export function cleanDisplayText(input: string | undefined | null): string {
  if (!input) return '';
  return stripMarkdownToLine(decodeHtmlEntities(input));
}
